import type {FormInterview, JOB_CUSTOM_FIELD} from 'src/types/crm';
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import {useDispatch, useSelector} from 'react-redux';

import * as reduxApi from 'src/utils/redux-api-v2';

import User from 'src/components/crm/lib/user/user';
import {selectHasCrmPermission, selectUserInfo} from 'src/selectors/crm';
import {formatMomentDateWithFormat} from 'src/utils/date-time';

import {
  Toast,
  TOAST_SEMANTIC,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {
  CollapsibleCard,
  CollapsibleCardAction,
  CollapsibleCardContent,
} from '@spaced-out/ui-design-system/lib/components/CollapsibleCard';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@spaced-out/ui-design-system/lib/components/Modal';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';
import {
  BodyMedium,
  SubTitleMedium,
  SubTitleSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Shimmer} from '@spaced-out/ui-design-system/lib/components/Shimmer';
import ViewHistoryPanel from 'src/components/crm/applicants/body/tabComponents/forms/view-history-panel/view-history-panel';

import {Banner} from '@spaced-out/ui-design-system/lib/components/Banner';
import ViewCustomFields from 'src/components/crm/lib/custom-fields/view-custom-fields';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import CustomFormPanel from './custom-form-panel';
import moment from 'moment';

import css from './candidate-response-form.css';


const CandidateResponseForm = ({
  formInterview,
  applicationId,
  isPublicLink,
  candidateName = '',
  reloadCandidateData,
  showActionButtons = true,
}: {
  formInterview: FormInterview;
  applicationId: number;
  isPublicLink?: boolean;
  candidateName?: string;
  showActionButtons?: boolean;
  reloadCandidateData: () => void;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const userInfo = useSelector(selectUserInfo);
  const editCandidateForm = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.EDIT_CANDIDATE_FORM,
  );
  const dispatch = useDispatch();
  const [isCardOpen, setIsCardOpen] = React.useState(true);
  const [loadingStep, setLoadingStep] = React.useState(false);
  const [isCustomFormPanelOpen, setIsCustomFormPanelOpen] =
    React.useState(false);
  const [viewHistoryPanelOpen, setViewHistoryPanelOpen] = React.useState(false);
  const {interviewCandidateFormField, stepId, status} = formInterview;
  const {
    validationStatus,
    validatorId,
    validationComment,
    updatedOn,
    id,
    stepCandidateId,
  } = interviewCandidateFormField;

  const [stepName, setStepName] = React.useState<any>(null);
  const [showApproveDeclineModal, setShowApproveDeclineModal] =
    React.useState(false);
  const [approveDeclineModalType, setApproveDeclineModalType] =
    React.useState<any>(null);

  const renderAnswer = (field: JOB_CUSTOM_FIELD) => {
    if (!interviewCandidateFormField.customFields) {
      return <BodyMedium>{`'${t('NA', 'N/A')}'`}</BodyMedium>;
    }
    const customField = interviewCandidateFormField.customFields.find(
      (customField) => customField.id === field.customFieldId,
    );
    if (!customField) {
      return <BodyMedium>{`'${t('NA', 'N/A')}'`}</BodyMedium>;
    }

    return <ViewCustomFields field={customField} />;
  };

  React.useEffect(() => {
    const fetchStep = async () => {
      try {
        setLoadingStep(true);
        const stepData = await dispatch(
          reduxApi.get(
            `api/job-steps/${stepId}`,
            {},
            {
              apiPath: '/trmapi/',
            },
          ),
        );
        setStepName(stepData.name);
      } catch (e: any) {
        throw e;
      } finally {
        setLoadingStep(false);
      }
    };

    if (stepId) {
      fetchStep();
    }
  }, [stepId]);

  const approvalStatusString =
    `${t('Submitted_by_candidate_on', 'Submitted by candidate on')} ` +
    moment(updatedOn).format('DD MMMM, YYYY');

  const validatorUser = <User userId={validatorId} />;

  return (
    <>
      <CollapsibleCard
        classNames={{
          wrapper: '',
        }}
        isOpen={isCardOpen}
        onChange={() => setIsCardOpen(!isCardOpen)}
        title={
          loadingStep ? (
            <Shimmer height="20px" width="200px" />
          ) : (
            <div className={css.candidateResponseFormTitle}>
              <SubTitleSmall>{stepName}</SubTitleSmall>
              <Chip semantic="information">
                {t('Candidate_Form', 'Candidate Form')}
              </Chip>
            </div>
          )
        }
      >
        <CollapsibleCardContent className={css.cardContentWithBanner}>
          {validationStatus === 'APPROVED' ? (
            // @ts-expect-error - TS2769 - No overload matches this call.
            <Banner
              semantic="success"
              actionText={
                showActionButtons ? t('View_History', 'View History') : ''
              }
              onAction={() => setViewHistoryPanelOpen(true)}
            >
              {}
              {t('Approved_by', 'Approved by')} {validatorUser} {t('on', 'on')}{' '}
              {moment(updatedOn).format('DD MMMM, YYYY')}
              <br />
              {t('Comment', 'Comment')}: {validationComment}
            </Banner>
          ) : validationStatus === 'REJECTED' ? (
            // @ts-expect-error - TS2769 - No overload matches this call.
            <Banner
              semantic="danger"
              actionText={
                showActionButtons ? t('View_History', 'View History') : ''
              }
              onAction={() => setViewHistoryPanelOpen(true)}
            >
              {}
              {t('Declined_by', 'Declined by')} {validatorUser} {t('on', 'on')}{' '}
              {moment(updatedOn).format('DD MMMM, YYYY')}
              <br />
              {t('Comment', 'Comment')}: {validationComment}
            </Banner>
          ) : (
            <Banner
              semantic="warning"
              actionText={
                showActionButtons ? t('View_History', 'View History') : ''
              }
              onAction={() => setViewHistoryPanelOpen(true)}
            >
              {approvalStatusString}
            </Banner>
          )}

          <div className={css.candidateResponseCardContent}>
            {interviewCandidateFormField.customForms &&
              interviewCandidateFormField.customForms[0].sections &&
              interviewCandidateFormField.customForms[0].sections.map(
                (section) => {
                  return (
                    <div className={css.formDisplaySectionContainer}>
                      <SubTitleMedium>{section.name}</SubTitleMedium>
                      <div className={css.lineBreak} />

                      {section.fields
                        .filter(
                          (field) => field.show !== 'OFF' && field.customField,
                        )
                        .map((field) => (
                          <div className={css.customField}>
                            <SubTitleSmall>{field.questionTag}</SubTitleSmall>
                            {renderAnswer(field)}
                          </div>
                        ))}
                    </div>
                  );
                },
              )}
          </div>
        </CollapsibleCardContent>
        {showApproveDeclineModal ? (
          <ApproveDeclineModal
            isOpen={showApproveDeclineModal}
            onClose={() => setShowApproveDeclineModal(false)}
            onSubmit={() => {
              reloadCandidateData();
            }}
            approveDeclineModalType={approveDeclineModalType}
            stepCandidateId={stepCandidateId}
            interviewCandidateFormId={id}
          />
        ) : null}
        {showActionButtons && (
          <CollapsibleCardAction>
            <div className={css.cardActionContainer}>
              {editCandidateForm && (
                <Button
                  type="tertiary"
                  size="small"
                  iconLeftName="pencil"
                  onClick={() => setIsCustomFormPanelOpen(true)}
                >
                  {t('Edit', 'Edit')}
                </Button>
              )}
              {validationStatus !== 'REJECTED' ? (
                <Button
                  type="tertiary"
                  size="small"
                  onClick={() => {
                    setShowApproveDeclineModal(true);
                    setApproveDeclineModalType(
                      validationStatus === 'APPROVED'
                        ? 'DECLINE_AND_RESEND'
                        : 'DECLINE',
                    );
                  }}
                >
                  {t('Discard_Resend', 'Discard & Resend')}
                </Button>
              ) : null}
              {validationStatus !== 'APPROVED' ? (
                <Button
                  size="small"
                  onClick={() => {
                    setShowApproveDeclineModal(true);
                    setApproveDeclineModalType('APPROVE');
                  }}
                >
                  {t('Approve', 'Approve')}
                </Button>
              ) : null}
            </div>
          </CollapsibleCardAction>
        )}
      </CollapsibleCard>
      {viewHistoryPanelOpen ? (
        <ViewHistoryPanel
          isOpen={viewHistoryPanelOpen}
          onClose={() => setViewHistoryPanelOpen(false)}
          formId={id}
        />
      ) : null}
      {isCustomFormPanelOpen ? (
        <CustomFormPanel
          isCandidate={true}
          formInterview={formInterview}
          isOpen={isCustomFormPanelOpen}
          onClose={() => {
            setIsCustomFormPanelOpen(false);
          }}
          formType="CANDIDATE_FORM"
        />
      ) : null}
    </>
  );
};

export const ApproveDeclineModal = ({
  isOpen,
  onClose,
  onSubmit,
  approveDeclineModalType,
  stepCandidateId,
  interviewCandidateFormId,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  approveDeclineModalType: string | null;
  stepCandidateId: number;
  interviewCandidateFormId: number;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = React.useState(false);
  const [comment, setComment] = React.useState('');

  const handleValidation = async (validationStatus: string) => {
    try {
      setIsLoading(true);
      await dispatch(
        reduxApi.post(
          `api/interview-candidate-form/${interviewCandidateFormId}/validate?interviewId=${stepCandidateId}`,
          {
            comment,
            validationStatus,
          },
          {},
          {
            apiPath: '/trmapi/',
          },
        ),
      );

      const action =
        validationStatus === 'APPROVED'
          ? t('Approved', 'Approved')
          : t('Declined', 'Declined');
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.success}>
          <ToastTitle>
            {`${t('Form', 'Form')} `}
            {action}
            {` ${t('Successfully', 'Successfully')}!`}
          </ToastTitle>
        </Toast>,
      );
      onSubmit();
      onClose();
    } catch (e: any) {
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>
            {t('Unable_to', 'Unable to')}{' '}
            {validationStatus === 'APPROVED'
              ? t('approve', 'approve')
              : t('decline', 'decline')}{' '}
            {t('form', 'form')}.
          </ToastTitle>
        </Toast>,
      );
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  const getModalTitle = () => {
    switch (approveDeclineModalType) {
      case 'APPROVE':
        return t('Approve_Form_Response', 'Approve Form Response');
      case 'DECLINE':
        return t('Discard_Response_Resend', 'Discard Response & Resend');
      case 'DECLINE_AND_RESEND':
        return t('Discard_Response_Resend', 'Discard Response & Resend');
      default:
        return '';
    }
  };

  const isDeclineType =
    approveDeclineModalType === 'DECLINE' ||
    approveDeclineModalType === 'DECLINE_AND_RESEND';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium">
      <ModalHeader onCloseButtonClick={onClose}>{getModalTitle()}</ModalHeader>
      <ModalBody className={css.approveDeclineModalBody}>
        <Textarea
          label={t('Reason', 'Reason')}
          placeholder={t('Add_reasons', 'Add reasons')}
          value={comment}
          required={isDeclineType}
          onChange={(e) => setComment(e.target.value)}
        />
        {(approveDeclineModalType === 'DECLINE_AND_RESEND' ||
          approveDeclineModalType === 'DECLINE') && (
          <BodyMedium color="secondary">{`${t(
            'Discarding_this_response_will_resend_the_form_to_the_candidate',
            'Discarding this response will resend the form to the candidate',
          )}.`}</BodyMedium>
        )}
      </ModalBody>
      <ModalFooter>
        <Button type="ghost" onClick={onClose}>
          {t('Cancel', 'Cancel')}
        </Button>
        {approveDeclineModalType === 'APPROVE' ? (
          <Button
            type="primary"
            onClick={() => handleValidation('APPROVED')}
            isLoading={isLoading}
          >
            {t('Approve', 'Approve')}
          </Button>
        ) : (
          <Button
            type="primary"
            disabled={comment === ''}
            onClick={() => handleValidation('REJECTED')}
            isLoading={isLoading}
          >
            {t('Discard_Resend', 'Discard & Resend')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default CandidateResponseForm;
